import { Const } from "../../../Const";
import { LayaGL } from "../../../layagl/LayaGL";
import { Color } from "../../../maths/Color";
import { Matrix } from "../../../maths/Matrix";
import { Vector3 } from "../../../maths/Vector3";
import { Vector4 } from "../../../maths/Vector4";
import { BaseRenderNode2D } from "../../../NodeRender2D/BaseRenderNode2D";
import { VertexMesh2D } from "../../../resource/Mesh2D";
import { Texture2D } from "../../../resource/Texture2D";
import { Pool } from "../../../utils/Pool";
import { ShaderDefines2D } from "../../../webgl/shader/d2/ShaderDefines2D";
import { Mesh2DRender } from "../Mesh2DRender";
import { Command2D } from "./Command2D";
export class DrawMesh2DCMD extends Command2D {
    static create(mesh, mat, texture, color, material) {
        let cmd = DrawMesh2DCMD._pool.take();
        cmd.mesh = mesh;
        cmd.material = material || Mesh2DRender.mesh2DDefaultMaterial;
        cmd.texture = texture;
        cmd.color = color;
        cmd._setMatrix(mat);
        return cmd;
    }
    constructor() {
        super();
        this._renderElements = [];
        this._renderColor = new Color(1, 1, 1, 1);
        this._tilingOffset = new Vector4(0, 0, 1, 1);
        this._drawElementData = LayaGL.render2DRenderPassFactory.createDraw2DElementCMDData();
        this._shaderData = LayaGL.renderDeviceFactory.createShaderData();
        this._shaderData.addDefine(BaseRenderNode2D.SHADERDEFINE_BASERENDER2D);
        let temp = Vector4.TEMP.setValue(0, 0, 0, 0);
        this._shaderData.setVector(ShaderDefines2D.UNIFORM_CLIPMATPOS, temp);
        temp.x = temp.w = Const.MAX_CLIP_SIZE;
        this._shaderData.setVector(ShaderDefines2D.UNIFORM_CLIPMATDIR, temp);
        this._needUpdateElement = true;
        this._matrix = new Matrix();
    }
    _setMatrix(value) {
        value ? value.copyTo(this._matrix) : Matrix.EMPTY.copyTo(this._matrix);
        let mat = this._matrix;
        let vec3 = Vector3.TEMP;
        vec3.x = mat.a;
        vec3.y = mat.c;
        vec3.z = mat.tx;
        this._shaderData.setVector3(ShaderDefines2D.UNIFORM_NMATRIX_0, vec3);
        vec3.x = mat.b;
        vec3.y = mat.d;
        vec3.z = mat.ty;
        this._shaderData.setVector3(ShaderDefines2D.UNIFORM_NMATRIX_1, vec3);
    }
    set material(value) {
        if (value == this.material)
            return;
        if (!value)
            value = Mesh2DRender.mesh2DDefaultMaterial;
        this._material = value;
        this._needUpdateElement = true;
    }
    get material() {
        return this._material;
    }
    set mesh(value) {
        if (value == this.mesh)
            return;
        if (this._mesh) {
            let defines = [];
            VertexMesh2D.getMeshDefine(this._mesh, defines);
            for (var i = 0, n = defines.length; i < n; i++)
                this._shaderData.removeDefine(defines[i]);
        }
        if (value) {
            let defines = [];
            VertexMesh2D.getMeshDefine(value, defines);
            for (var i = 0, n = defines.length; i < n; i++)
                this._shaderData.addDefine(defines[i]);
        }
        this._mesh = value;
        this._needUpdateElement = true;
    }
    get mesh() {
        return this._mesh;
    }
    set texture(value) {
        value = value ? value : Texture2D.whiteTexture;
        if (value.gammaCorrection != 1) {
            this._shaderData.addDefine(ShaderDefines2D.GAMMATEXTURE);
        }
        else {
            this._shaderData.removeDefine(ShaderDefines2D.GAMMATEXTURE);
        }
        this._texture = value;
        this._shaderData.setVector(BaseRenderNode2D.TILINGOFFSET, this._tilingOffset);
        this._shaderData.setTexture(BaseRenderNode2D.BASERENDER2DTEXTURE, value);
    }
    get texture() {
        return this._texture;
    }
    set color(value) {
        this._color = value;
        let a = value.a;
        let renderColor = this._renderColor;
        renderColor.setValue(value.r * a, value.g * a, value.b * a, a);
        this._shaderData.setColor(BaseRenderNode2D.BASERENDER2DCOLOR, renderColor);
    }
    get color() {
        return this._color;
    }
    getRenderCMD() {
        return this._drawElementData;
    }
    run() {
        if (this._needUpdateElement) {
            let elementLength = this._renderElements.length;
            let subMeshCount = this._mesh.subMeshCount;
            let length = Math.max(elementLength, subMeshCount);
            for (let i = 0; i < length; i++) {
                let subMesh = this._mesh.getSubMesh(i);
                let element = this._renderElements[i];
                if (subMesh) {
                    if (!element) {
                        element = this._renderElements[i] = LayaGL.render2DRenderPassFactory.createRenderElement2D();
                    }
                    element.nodeCommonMap = ["BaseRender2D"];
                    element.geometry = subMesh;
                    element.renderStateIsBySprite = false;
                    element.value2DShaderData = this._shaderData;
                    element.materialShaderData = this._material.shaderData;
                    element.subShader = this._material._shader.getSubShaderAt(0);
                }
                else {
                    element.destroy();
                }
            }
            this._renderElements.length = length;
            this._drawElementData.setRenderelements(this._renderElements);
            this._needUpdateElement = false;
        }
    }
    recover() {
        DrawMesh2DCMD._pool.recover(this);
        super.recover();
        this.material = null;
        this.texture = null;
        this.mesh = null;
    }
    destroy() {
        super.destroy();
        this._shaderData.destroy();
        this._shaderData = null;
        this._mesh = null;
        this._material = null;
    }
}
DrawMesh2DCMD._pool = Pool.createPool(DrawMesh2DCMD);
